import find from 'lodash/find';
import pickBy from 'lodash/pickBy';
import toPairs from 'lodash/toPairs';
import isNumber from 'lodash/isNumber';
import fromPairs from 'lodash/fromPairs';
import isBoolean from 'lodash/isBoolean';
import map from 'lodash/map';
import type { Attributes, FeatureDefinition, JSONValue } from '@growthbook/growthbook-react';
import { GrowthBook } from '@growthbook/growthbook-react';
import type { WidenPrimitives } from '@growthbook/growthbook';

import { getDebugLogger } from '@jerry-serverless/console/debug';

import type { AppData } from '../../types/app_data';
import { EnabledABTests } from '../enabled_features/ab_tests';
import type { ABTest } from '../enabled_features/types';

import { trackABTestOn, trackABTestVariation, ssrTrackABTestVariation } from './utils';

const isServer = typeof window === 'undefined';

const log = getDebugLogger('GrowthBook');

const CLEVERTAP_EVENTS = ['quote_journey_clevertap'];

export let growthBookInstance: GrowthBook | undefined;

// These attributes are available for bucketing in GB
interface GrowthbookAttributes {
  id?: string;
  phone?: string;
  environment?: string;
  address?: { state?: string };
  deviceId?: string;
  createdAt?: string;
  appVersion?: number;
  path?: string;
  sessionId?: string;
}
/**
 * Get the GrowthBook attributes from appData
 *
 * @param appData - The context AppData
 * @returns The GrowthBook attributes
 */
const getAttributesFromAppData = (appData: AppData) => {
  const attributes: GrowthbookAttributes = {
    ...appData,
    environment: process.env.NEXT_PUBLIC_STAGE,
  };

  return attributes;
};

/**
 * Set the GrowthBook features
 *
 * @param growthbook - Instance of GrowthBook
 * @param featuresData - The GrowthBook features data
 * @param forcedFeaturesData - The GrowthBook forced features data
 */
export const setGrowthBookFeatures = (
  growthbook: GrowthBook,
  featuresData?: Record<string, FeatureDefinition>,
  forcedFeaturesData?: Record<string, number | boolean>
) => {
  if (featuresData) {
    growthbook.setFeatures(featuresData);
    log('GrowthBook Feature Data', featuresData);
  }

  if (forcedFeaturesData) {
    const forcedVariations = pickBy(forcedFeaturesData, isNumber);
    growthbook.setForcedVariations(forcedVariations);
    log('GrowthBook Forced Variations', forcedVariations);
    growthbook.setForcedFeatures(new Map(toPairs(forcedFeaturesData)));
    log('GrowthBook Forced Feature Data', forcedFeaturesData);
  }
};

/**
 * Generate a GrowthBook instance by params
 *
 * @param config - The GrowthBook constructor context
 * @returns The GrowthBook instance
 */
export const createGrowthBook = (
  config: {
    attributes?: Attributes;
    features?: Record<string, FeatureDefinition>;
    forcedFeaturesData?: Record<string, number | boolean>;
  } = {}
) => {
  const { attributes, features, forcedFeaturesData } = config;
  // We should not cache GrowthBook instance on server side due to shared runtime
  // and each user should have it;s own GrowthBook instance
  if (!growthBookInstance) {
    growthBookInstance = new GrowthBook({
      apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
      clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
      enableDevMode: process.env.NEXT_PUBLIC_STAGE !== 'prod',
      attributes,
      trackingCallback: async (experiment, result) => {
        // Skip events from SSR to prevent sending wrong AB test variation result
        if (isServer) {
          return;
        }
        const featureKey = experiment.key;
        const abTest = find(EnabledABTests, (test) => test.storageKey === featureKey);
        if (abTest) {
          // For A/B Experiment, track the variation
          if (result?.variationId !== undefined) {
            await trackABTestVariation(abTest, result.variationId);
          } else if (isBoolean(result.value)) {
            // For Percentage Rollout, track when the value is the force on
            await trackABTestOn(abTest);
          }
        } else {
          log('No ABTest found for experiment', featureKey);
        }
      },
    });
  }
  if (attributes) {
    growthBookInstance.setAttributes(attributes);
  }

  setGrowthBookFeatures(growthBookInstance, features, forcedFeaturesData);
  growthBookInstance.debug = !isServer && process.env.NEXT_PUBLIC_STAGE !== 'prod';

  return growthBookInstance;
};

/**
 * Generate a GrowthBook instance by appData
 *
 * @param appData - AppData
 * @returns GrowthBook instance
 */
export const createGrowthBookFromAppData = (appData: AppData) => {
  const attributes = getAttributesFromAppData(appData);

  return createGrowthBook({
    attributes,
    features: appData.growthBookFeatureData,
    /**
     * Why we need this?
     * We use some special URL params like 'test-{featureKey}=1' or cookie to force some features variation
     * So we need to sync this with GrowthBook, then it will always return the same forced values to us.
     * Also the GrowthBook's track events can be correct.
     */
    forcedFeaturesData: appData.abTestData,
  });
};

/**
 * Exports all current AB test variables
 */
export const getABTestReadableState = (): Record<string, string> => {
  if (!growthBookInstance) {
    return {};
  }
  return fromPairs(
    map(CLEVERTAP_EVENTS, (abTestName) => {
      const name = `ABTEST_${abTestName}`;
      const feature = growthBookInstance?.feature(abTestName);
      const value = feature?.value;
      const variations = feature?.experiment?.variations || [];
      if (isBoolean(value) || (variations.length === 2 && isNumber(value))) {
        return [name, value ? 'Enabled' : 'Disabled'];
      } else if (isNumber(value) && value >= 0 && value <= 4) {
        const states = ['Control', 'A', 'B', 'C', 'D'];

        return [name, states[value]];
      } else {
        return [name, String(value)];
      }
    })
  );
};

/**
 * `trackingCallback` of the global growth book instance doesn't send events during SSR. This function helps get
 * feature value during SSR and send experiment test events.
 */
export async function ssrGetFeatureValue<T extends JSONValue>(
  appData: AppData,
  abTest: ABTest,
  defaultValue: T
): Promise<WidenPrimitives<T>> {
  const growthBook = createGrowthBookFromAppData(appData);
  const test = growthBook.evalFeature(abTest.storageKey);
  if (test?.experimentResult?.variationId !== undefined) {
    await ssrTrackABTestVariation(abTest, test.experimentResult.variationId, {
      userID: appData.id,
      sessionID: appData.sessionId,
    });
  }
  // Retrieve the value using `getFeatureValue`. Not using the experiment result because there might be forced value.
  const value = growthBook.getFeatureValue(abTest.storageKey, defaultValue);
  console.log('ssrGetFeatureValue', abTest.storageKey, value, appData.id, appData.sessionId);
  return value;
}
